'use client';

import * as React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

import { Skeleton } from '@workspace/ui/components/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@workspace/ui/components/tabs';

interface CategoryBreakdownChartProps {
  categoryBreakdown: Record<string, { total: number; average: number; count: number }>;
  isLoading?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  VENUE: 'Venue',
  PHOTOGRAPHER: 'Photography',
  VIDEOGRAPHER: 'Videography',
  DJ_BAND: 'Music & DJ',
  FLORIST: 'Florals',
  CATERER: 'Catering',
  PLANNER: 'Planning',
  BEAUTY: 'Beauty',
  CAKES_DESSERT: 'Cake & Desserts',
  TRANSPORTATION: 'Transportation',
  GENERAL: 'General',
};

const CATEGORY_COLORS: Record<string, string> = {
  CATERER: '#3b82f6',
  VENUE: '#64748b',
  PHOTOGRAPHER: '#475569',
  VIDEOGRAPHER: '#64748b',
  PLANNER: '#a855f7',
  FLORIST: '#6366f1',
  DJ_BAND: '#475569',
  GENERAL: '#94a3b8',
  TRANSPORTATION: '#10b981',
  BEAUTY: '#ec4899',
  CAKES_DESSERT: '#f59e0b',
};

const AXIS_COLORS = {
  grid: '#6b728033',
  text: '#9ca3af',
  labelText: '#d1d5db',
  border: '#374151',
};

interface ChartDataItem {
  category: string;
  label: string;
  average: number;
  total: number;
  count: number;
  color: string;
}

function prepareChartData(
  categoryBreakdown: Record<string, { total: number; average: number; count: number }>
): ChartDataItem[] {
  return Object.entries(categoryBreakdown)
    .map(([category, data]) => ({
      category,
      label: CATEGORY_LABELS[category] || category,
      average: data.average,
      total: data.total,
      count: data.count,
      color: CATEGORY_COLORS[category] || '#6b7280',
    }))
    .sort((a, b) => b.average - a.average);
}

export function CategoryBreakdownChart({
  categoryBreakdown,
  isLoading,
}: CategoryBreakdownChartProps): React.JSX.Element {
  const data = React.useMemo(() => prepareChartData(categoryBreakdown), [categoryBreakdown]);

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center text-muted-foreground">
        No category data available.
      </div>
    );
  }

  return (
    <Tabs defaultValue="bar" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="bar">Bar Chart</TabsTrigger>
        <TabsTrigger value="pie">Pie Chart</TabsTrigger>
      </TabsList>

      <TabsContent value="bar">
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke={AXIS_COLORS.grid} horizontal={true} vertical={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: AXIS_COLORS.text }}
                tickLine={false}
                axisLine={{ stroke: AXIS_COLORS.border }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 12, fill: AXIS_COLORS.labelText }}
                tickLine={false}
                axisLine={false}
                width={90}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const chartData = payload[0].payload as ChartDataItem;
                  return (
                    <div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-lg">
                      <p className="font-medium">{chartData.label}</p>
                      <p className="text-sm">
                        Average: <span className="font-medium">${chartData.average.toLocaleString()}</span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Based on {chartData.count.toLocaleString()} items
                      </p>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="average"
                radius={[0, 4, 4, 0]}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    className="transition-opacity hover:opacity-80"
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </TabsContent>

      <TabsContent value="pie">
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={140}
                paddingAngle={2}
                dataKey="average"
                nameKey="label"
                animationDuration={800}
                animationEasing="ease-out"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    className="transition-opacity hover:opacity-80"
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const pieData = payload[0].payload as ChartDataItem;
                  const totalAll = Object.values(categoryBreakdown).reduce((sum, cat) => sum + cat.average, 0);
                  const percentage = totalAll > 0 ? ((pieData.average / totalAll) * 100).toFixed(1) : '0';
                  return (
                    <div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-lg">
                      <p className="font-medium">{pieData.label}</p>
                      <p className="text-sm">
                        ${pieData.average.toLocaleString()} ({percentage}%)
                      </p>
                    </div>
                  );
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span className="text-sm text-foreground">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </TabsContent>
    </Tabs>
  );
}

